import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cross2Icon } from '@radix-ui/react-icons';
import type { PropertyFilters } from './filter-panel/constants';
import { FilterBody } from './filter-panel/FilterBody';
import { Button, Text, FilterPanelShell } from '@/shared/ui';
import { viewportOverlayStyle } from '@/shared/ui/utils/viewport-overlay';

export type { PropertyFilters } from './filter-panel/constants';

interface FilterPanelProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: PropertyFilters) => void;
  onClear: () => void;
  initialFilters?: PropertyFilters;
}

/**
 * Renders the sliding property filter panel with apply and clear actions.
 * @returns The filter panel element, or null when closed.
 */
export const FilterPanel: React.FC<FilterPanelProps> = ({
  open,
  onClose,
  onApply,
  onClear,
  initialFilters = {},
}) => {
  const { t } = useTranslation('features/properties');
  const [filters, setFilters] = useState<PropertyFilters>(initialFilters);

  // The panel keeps its own draft, but the APPLIED filters can change while it
  // is closed -- the chip row's "Limpar todos" clears them without ever going
  // through this panel. Re-seed the draft from `initialFilters` each time the
  // panel opens so a reopened panel always mirrors what is actually applied,
  // instead of showing a stale pre-clear selection.
  useEffect(() => {
    if (open) setFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  const toggleInArray = (field: keyof PropertyFilters, value: string) => {
    const current = (filters[field] as string[] | undefined) || [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    setFilters({ ...filters, [field]: next });
  };

  const activeCount = Object.values(filters).filter((v) => (Array.isArray(v) ? v.length > 0 : !!v)).length;

  if (!open) return null;

  return (
    <FilterPanelShell
      testIdPrefix=""
      usePortal
      layout="nested"
      title={t('filters.title')}
      onClose={onClose}
      onClear={() => { setFilters({}); onClear(); }}
      onApply={() => { onApply(filters); onClose(); }}
      activeCount={activeCount}
      clearLabel={t('filters.clear')}
      applyLabel={t('filters.applyWithCount', { count: activeCount })}
      closeIcon={<Cross2Icon width={14} height={14} />}
      ButtonComponent={Button}
      TextComponent={({ children, style, className }) => (
        <Text as="span" className={className ?? 'text-sm font-bold'} style={style ?? { color: 'var(--seed-base)' }}>
          {children}
        </Text>
      )}
      backdropStyle={viewportOverlayStyle}
      panelClassName="flex w-full flex-col overflow-hidden"
      panelStyle={{
        maxWidth: '340px',
        height: '100%',
        background: 'var(--surface-primary)',
        boxShadow: 'var(--shadow-lg)',
        fontFamily: 'var(--font-family)',
      }}
      headerClassName="flex items-center justify-between px-(--space-4) pt-(--space-1) pb-(--space-2)"
      headerStyle={{ borderBottom: '1px solid var(--border-subtle)' }}
      closeButtonClassName="flex items-center justify-center w-7 h-7 rounded-(--radius-md) transition-colors hover:bg-[var(--surface-hover)]"
      closeButtonStyle={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}
      footerClassName="flex gap-(--space-2) px-(--space-4) py-(--space-3) sm:pb-(--space-3) pb-(--space-20)"
      footerStyle={{ borderTop: '1px solid var(--border-subtle)' }}
      clearButtonClassName="flex-1 py-(--space-2) text-xs font-semibold rounded-(--radius-md) cursor-pointer"
      clearButtonStyle={{ background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-family)' }}
      applyButtonClassName="flex-1 py-(--space-2) text-xs font-bold rounded-(--radius-md) cursor-pointer"
      applyButtonStyle={{ background: 'var(--button-primary-bg)', color: 'var(--button-primary-text)', border: 'none', fontFamily: 'var(--font-family)' }}
    >
      <FilterBody filters={filters} setFilters={setFilters} toggleInArray={toggleInArray} />
    </FilterPanelShell>
  );
};
